import { useLayoutEffect, useState, type RefObject } from 'react';

/** Minimum row height before packing more rows into the scroll band. */
export const MIN_CATALOG_ROW = 44;

type FitOptions = {
  /** Query for direct row children inside the scrollport. */
  rowSelector?: string;
  minRow?: number;
  cssVar?: string;
  /** y proximity snap (catalog lists + mobile release tracks). */
  snap?: boolean;
};

/** Estimate equal row height for a band; used to preset CSS before paint.
 * Always packs the maximum integer rows that fit — never stretches a short
 * list (e.g. a 1-track release) to fill the whole band.
 * Exact division (not floored) so N rows fill the scrollport with no peek.
 */
export function estimateRowHeight(
  bandHeight: number,
  minRow = MIN_CATALOG_ROW,
): number {
  if (bandHeight <= 0) return minRow;
  const rowCount = Math.max(1, Math.floor(bandHeight / minRow));
  return bandHeight / rowCount;
}

function readSafeAreaInsets(): { top: number; bottom: number } {
  if (typeof document === 'undefined') return { top: 0, bottom: 0 };
  const probe = document.createElement('div');
  probe.style.cssText =
    'position:fixed;visibility:hidden;pointer-events:none;' +
    'padding:env(safe-area-inset-top) 0 env(safe-area-inset-bottom)';
  document.documentElement.appendChild(probe);
  const cs = getComputedStyle(probe);
  const top = parseFloat(cs.paddingTop) || 0;
  const bottom = parseFloat(cs.paddingBottom) || 0;
  probe.remove();
  return { top, bottom };
}

/**
 * Catalog scroll band ≈ viewport minus mobile chrome.
 * Installed iOS PWA: include safe-area (status + home indicator) and tab bar,
 * not the desktop footer estimate — that left the skeleton short vs loaded list.
 */
export function estimateCatalogBandHeight(): number {
  if (typeof window === 'undefined') return MIN_CATALOG_ROW * 12;
  const mobile = window.matchMedia('(max-width: 640px)').matches;
  const vh = window.innerHeight;
  if (!mobile) {
    const chrome = 100 + 62 + 90;
    return Math.max(MIN_CATALOG_ROW, vh - chrome);
  }
  const { top: saTop, bottom: saBottom } = readSafeAreaInsets();
  // header: 8 + SA top + 44 identity; tab: ~49 + SA bottom; toolbar + labels ~56
  const chrome = 8 + 44 + saTop + 49 + saBottom + 56;
  return Math.max(MIN_CATALOG_ROW, vh - chrome);
}

/** Ghost rows to fill one scrollport (+ buffer) so PWA skeleton isn’t short. */
export function estimateCatalogSkeletonRows(extra = 2): number {
  const band = estimateCatalogBandHeight();
  const rowH = estimateRowHeight(band);
  return Math.max(12, Math.ceil(band / Math.max(rowH, MIN_CATALOG_ROW)) + extra);
}

/**
 * Preset --catalog-row-height on :root from the viewport so skeleton/list
 * paint at the correct height before measure runs.
 */
export function presetCatalogRowHeight(): number {
  if (typeof window === 'undefined') return MIN_CATALOG_ROW;
  const band = estimateCatalogBandHeight();
  const next = estimateRowHeight(band);
  document.documentElement.style.setProperty('--catalog-row-height', `${next}px`);
  return next;
}

/**
 * Fit an integer number of equal-height rows into the scrollport
 * so the last visible row is never clipped mid-row.
 * Also clears the bottom border on the last fully visible row so it doesn't
 * double up against the footer rule.
 *
 * Snap + edge updates match the deployed catalog fit: `y proximity` while
 * enabled, edges refreshed on scroll via rAF (including release track lists).
 */
export function useCatalogRowFit(
  scrollRef: RefObject<HTMLElement | null>,
  itemCount: number,
  enabled = true,
  options?: FitOptions,
) {
  const rowSelector = options?.rowSelector ?? ':scope > .catalog-row, :scope > .skeleton-list-row';
  const minRow = options?.minRow ?? MIN_CATALOG_ROW;
  const cssVar = options?.cssVar ?? '--catalog-row-height';
  const snap = options?.snap !== false;
  const [rowHeight, setRowHeight] = useState(() =>
    typeof window !== 'undefined' ? presetCatalogRowHeight() : minRow,
  );

  useLayoutEffect(() => {
    const el = scrollRef.current;
    if (!el || !enabled) {
      if (el && cssVar === '--catalog-row-height') {
        // Keep a viewport preset so the next catalog paint is stable
        presetCatalogRowHeight();
      } else if (el) {
        el.style.removeProperty(cssVar);
      }
      if (el) {
        el.style.scrollSnapType = '';
        el.querySelectorAll('.edge-bottom').forEach((row) => {
          row.classList.remove('edge-bottom');
        });
      }
      return;
    }

    let edgeRow: HTMLElement | null = null;
    const updateEdges = () => {
      const rows = el.querySelectorAll<HTMLElement>(rowSelector);
      if (!rows.length) {
        edgeRow?.classList.remove('edge-bottom');
        edgeRow = null;
        return;
      }
      const port = el.getBoundingClientRect();
      let bottomVisible: HTMLElement | null = null;
      for (let i = 0; i < rows.length; i++) {
        const row = rows[i]!;
        const r = row.getBoundingClientRect();
        if (r.bottom > port.top + 0.5 && r.top < port.bottom - 0.5) {
          bottomVisible = row;
        }
      }
      if (edgeRow === bottomVisible) return;
      edgeRow?.classList.remove('edge-bottom');
      bottomVisible?.classList.add('edge-bottom');
      edgeRow = bottomVisible;
    };

    const desktop =
      typeof window !== 'undefined' &&
      window.matchMedia('(min-width: 641px)').matches;

    const fit = () => {
      // Pack equal rows into the full scrollport (labels → tab/footer border).
      const h = el.clientHeight;
      if (h <= 0) {
        if (cssVar === '--catalog-row-height') {
          const preset = presetCatalogRowHeight();
          setRowHeight((prev) => (Math.abs(prev - preset) < 0.05 ? prev : preset));
        }
        return;
      }
      const next = estimateRowHeight(h, minRow);
      el.style.setProperty(cssVar, `${next}px`);
      if (cssVar === '--catalog-row-height') {
        document.documentElement.style.setProperty(cssVar, `${next}px`);
      }
      // Proximity + normal stop: soft settle (desktop CSS eases the snap).
      el.style.scrollSnapType = snap && itemCount > 0 ? 'y proximity' : '';
      setRowHeight((prev) => (Math.abs(prev - next) < 0.05 ? prev : next));
      updateEdges();
    };

    let edgeRaf = 0;
    let edgeTimer = 0;
    const onScrollEnd = () => {
      if (edgeTimer) {
        window.clearTimeout(edgeTimer);
        edgeTimer = 0;
      }
      updateEdges();
    };
    const onScroll = () => {
      // Desktop: wait for snap settle — per-frame class churn makes proximity
      // feel sticky/janky with a mouse wheel.
      if (desktop) {
        if (edgeTimer) window.clearTimeout(edgeTimer);
        edgeTimer = window.setTimeout(onScrollEnd, 48);
        return;
      }
      if (edgeRaf) return;
      edgeRaf = requestAnimationFrame(() => {
        edgeRaf = 0;
        updateEdges();
      });
    };

    fit();
    const ro = new ResizeObserver(fit);
    ro.observe(el);
    el.addEventListener('scrollend', onScrollEnd);
    el.addEventListener('scroll', onScroll, { passive: true });
    window.addEventListener('resize', fit);
    return () => {
      ro.disconnect();
      el.removeEventListener('scrollend', onScrollEnd);
      el.removeEventListener('scroll', onScroll);
      window.removeEventListener('resize', fit);
      if (edgeRaf) cancelAnimationFrame(edgeRaf);
      if (edgeTimer) window.clearTimeout(edgeTimer);
      el.style.scrollSnapType = '';
      edgeRow?.classList.remove('edge-bottom');
      edgeRow = null;
    };
  }, [scrollRef, itemCount, enabled, rowSelector, minRow, cssVar, snap]);

  return rowHeight;
}
